import axios from "axios";
import { API_URL, getHeaders } from "../config";

/**
 * Per-content resume/progress. Both calls are best-effort: any failure is
 * swallowed so the player/viewers never break if progress can't be saved or
 * fetched. Purely additive — does not affect the existing history endpoints.
 */

/**
 * The learning path the learner is currently working through, set when they
 * open a step from their plan (see lib/learning.js).
 *
 * It rides along on every beat so a path can keep its own run: material
 * finished months ago doesn't count as progress through a path assigned today.
 * Sent as a HINT only — the server credits it solely after checking that this
 * learner holds that path and that the path really contains this content, so a
 * stale value is ignored rather than believed.
 */
const activePathId = () => {
  try {
    return localStorage.getItem("activePathId") || null;
  } catch {
    return null;
  }
};

export const getProgress = async (contentType, contentId) => {
  try {
    const pathId = activePathId();
    const { data } = await axios.get(
      `${API_URL}/api/progress?contentType=${contentType}&contentId=${contentId}` +
        (pathId ? `&pathId=${encodeURIComponent(pathId)}` : ""),
      { headers: getHeaders().headers }
    );
    return data?.data || null;
  } catch (e) {
    return null;
  }
};

/**
 * Report OBSERVATIONS (dwell, pages reached, playback advanced). The server
 * evaluates them and returns its verdict — the client never asserts completion.
 * Returns the verdict, or null if the beat couldn't be delivered.
 */
/**
 * The learner's own record for one lesson, or for a standalone assessment.
 *
 * Exactly one of `pptItemId` / `assessmentId` is sent: a lesson has reading and
 * video to report alongside its attempts, a standalone assessment has only the
 * attempts. Same response shape either way, so one component renders both.
 */
export const getReview = async ({ pptItemId, assessmentId }) => {
  const { data } = await axios.get(`${API_URL}/api/progress/review`, {
    params: pptItemId ? { pptItemId } : { assessmentId },
    headers: getHeaders().headers,
  });
  return data?.data || null;
};

/**
 * The answer sheet for one attempt — every question with what the learner put
 * and what was expected.
 *
 * Fetched on demand rather than with the summary: a learner with several
 * attempts across several assessments would otherwise pull every question of
 * every paper to render a table of four columns.
 */
export const getAttempt = async (attemptId) => {
  const { data } = await axios.get(
    `${API_URL}/api/progress/review/attempt/${attemptId}`,
    { headers: getHeaders().headers }
  );
  return data?.data || null;
};

export const saveProgress = async (payload) => {
  try {
    const pathId = payload?.pathId || activePathId();
    const { data } = await axios.post(
      `${API_URL}/api/progress`,
      pathId ? { ...payload, pathId } : payload,
      {
        headers: getHeaders().headers,
      }
    );
    return data?.data || null;
  } catch (e) {
    /* best-effort */
    return null;
  }
};
